import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Layers, Search, Building2, Users, Info, ArrowUpDown, Newspaper, Loader as Loader2, TrendingUp, Trash2, Upload, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Sentry } from '../../lib/sentry';
import { useApi } from '../../lib/useApi';
import { getCached, setCache } from '../../lib/queryCache';
import type { Account, TerritoryPlan } from '../../types';
import { getLogoUrl, resolveLogoUrl } from '../../lib/logo';
import { logAccountActivity } from '../../lib/activityLog';
import { openAdminOrgSettings } from '../../lib/adminApp';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import NewsroomSidebar from '../../components/account/NewsroomSidebar';
import TriggerPill from '../../components/account/triggers/TriggerPill';
import BulkAccountUpload from '../../components/account/BulkAccountUpload';
import BulkUploadProgressBar from '../../components/account/BulkUploadProgressBar';
import TeamDrillDown from '../../components/territory/TeamDrillDown';

export default function Accounts() {
  const { organisation, user, membership } = useAuth();
  const { apiGet, apiPost, apiDelete } = useApi();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountRoles, setAccountRoles] = useState<Record<string, string>>({});
  const [territoryPlan, setTerritoryPlan] = useState<TerritoryPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tieredView, setTieredView] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTierInfo, setShowTierInfo] = useState<string | null>(null);
  const [showNewsroom, setShowNewsroom] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [tiering, setTiering] = useState(false);
  const tieringLockRef = useRef(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadKey, setBulkUploadKey] = useState(0);
  const [objectiveAccountIds, setObjectiveAccountIds] = useState<Set<string>>(new Set());
  const [activeView, setActiveView] = useState<'my' | 'team'>('my');

  const isLeaderOrExec = membership?.role === 'leader' || membership?.role === 'executive';

  const loadData = useCallback(async () => {
    if (!organisation || !user) return;
    const cachedPlan = getCached<TerritoryPlan>(`territory_plans:${organisation.id}`);

    try {
      const { accounts: fetchedAccounts, account_roles: roles } = await apiGet<{
        accounts: Account[];
        account_roles: Record<string, string>;
      }>(`/v1/account?org_id=${organisation.id}`);
      setAccounts(fetchedAccounts || []);
      setAccountRoles(roles || {});
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'load_accounts' } });
      setAccounts([]);
    }

    if (!cachedPlan) {
      const { plan: planData } = await apiGet<{ plan: TerritoryPlan | null }>(`/v1/territory-plan?org_id=${encodeURIComponent(organisation.id)}`).catch(() => ({ plan: null }));
      if (planData) {
        setTerritoryPlan(planData);
        setCache(`territory_plans:${organisation.id}`, planData);
      }
    } else {
      setTerritoryPlan(cachedPlan);
    }

    setLoading(false);
  }, [organisation, user, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!organisation || !user) return;
    (async () => {
      const { count } = await apiGet<{ count: number }>(
        `/v1/newsroom?org_id=${encodeURIComponent(organisation.id)}`,
      ).catch(() => ({ count: 0 }));
      setAlertCount(count || 0);
    })();
  }, [organisation, user, apiGet]);

  useEffect(() => {
    const saved = localStorage.getItem('accountsView');
    if (saved === 'tiered') {
      setTieredView(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('accountsView', tieredView ? 'tiered' : 'standard');
  }, [tieredView]);

  const hasPlan = territoryPlan?.status === 'ready';
  const hasAnyTiered = accounts.some(a => a.tier && a.tier_run_at);

  const runLightweightTier = useCallback(async () => {
    if (!organisation || tieringLockRef.current) return;
    tieringLockRef.current = true;
    setTiering(true);
    try {
      await apiPost('/v1/tier-accounts', { org_id: organisation.id, mode: 'lightweight' });
      await loadData();
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'lightweight_tiering' } });
    } finally {
      setTiering(false);
      tieringLockRef.current = false;
    }
  }, [organisation, loadData, apiPost]);

  const handleTierToggle = async () => {
    if (tieredView) {
      setTieredView(false);
      return;
    }

    if (!hasPlan) {
      if (membership?.role === 'admin' && organisation) {
        openAdminOrgSettings(organisation.id);
      }
      return;
    }

    // Always re-tier on every toggle to tiered view (lightweight, no AI cost)
    setTieredView(true);
    await runLightweightTier();
  };

  const handleAccountAdded = (account: Account) => {
    setAccounts(prev => [account, ...prev]);
    setShowAddModal(false);

    // Poll for enrichment completion, then refresh to pick up auto-tier results
    if (hasPlan && organisation) {
      (async () => {
        try {
          const maxWait = 120000;
          const interval = 5000;
          const start = Date.now();
          await new Promise<void>((resolve) => {
            const check = async () => {
              if (Date.now() - start > maxWait) { resolve(); return; }
              try {
                const { account: fresh } = await apiGet<{ account: Account }>(`/v1/account/${account.id}?org_id=${organisation!.id}`);
                if (fresh?.enrichment_status === 'completed') {
                  setAccounts(prev => prev.map(a => a.id === fresh.id ? fresh : a));
                  resolve();
                  return;
                }
              } catch { /* ignore, retry */ }
              setTimeout(check, interval);
            };
            setTimeout(check, 10000);
          });
        } catch (err) { Sentry.captureException(err, { tags: { context: 'background_tiering' } }); }
      })();
    }
  };

  const handleDeleteAccount = async (accountId: string, companyName: string) => {
    if (!confirm(`Delete "${companyName}" and all its data? This cannot be undone.`)) return;
    try {
      await apiDelete(`/v1/account/${accountId}?org_id=${organisation!.id}`);
      setAccounts(prev => prev.filter(a => a.id !== accountId));
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'delete_account' } });
    }
  };

  const filtered = accounts.filter(a => a.company_name.toLowerCase().includes(searchQuery.toLowerCase()));

  const sortByScore = (arr: Account[]) =>
    [...arr].sort((a, b) => (b.tier_score || 0) - (a.tier_score || 0));

  const tieredAccounts = {
    A: sortByScore(filtered.filter(a => a.tier === 'A')),
    B: sortByScore(filtered.filter(a => a.tier === 'B')),
    C: sortByScore(filtered.filter(a => a.tier === 'C')),
    untiered: filtered.filter(a => !a.tier),
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="app-page">
      {isLeaderOrExec && (
        <div className="mb-6 flex w-fit items-center gap-1 border border-app-line bg-app-surface p-1">
          <button
            onClick={() => setActiveView('my')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeView === 'my' ? 'bg-app-surface text-app-ink shadow-none' : 'text-app-muted hover:text-app-ink'}`}
          >
            My Accounts
          </button>
          <button
            onClick={() => setActiveView('team')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeView === 'team' ? 'bg-app-surface text-app-ink shadow-none' : 'text-app-muted hover:text-app-ink'}`}
          >
            {membership?.role === 'executive' ? 'My Theatre' : 'My Team'}
          </button>
        </div>
      )}

      {isLeaderOrExec && activeView === 'team' ? (
        <TeamDrillDown />
      ) : (
      <>
      <div className="mb-8 flex flex-col gap-5 border-b border-app-line pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="app-kicker mb-2">Account intelligence</p>
          <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Accounts</h1>
          <p className="text-sm text-app-muted mt-1">{accounts.length} accounts in your territory</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowNewsroom(true)}
            className="btn-secondary !py-2.5 !px-4 !text-sm relative"
          >
            <Newspaper className="w-4 h-4 mr-2" /> Newsroom
            {alertCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-app-critical text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {alertCount > 9 ? '9+' : alertCount}
              </span>
            )}
          </button>
          <button
            onClick={handleTierToggle}
            disabled={tiering || (!hasPlan && membership?.role !== 'admin')}
            className={`btn-secondary !py-2.5 !px-4 !text-sm ${!hasPlan && membership?.role !== 'admin' ? 'opacity-50' : ''}`}
            title={!hasPlan ? (membership?.role === 'admin' ? 'Click to set up Territory Plan' : 'Ask your admin to set up the Territory Plan') : ''}
          >
            {tiering ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Tiering...</>
            ) : tieredView ? (
              <><ArrowUpDown className="w-4 h-4 mr-2" /> Standard View</>
            ) : hasPlan ? (
              <><Layers className="w-4 h-4 mr-2" /> {hasAnyTiered ? 'Tiered View' : 'Tier My Accounts'}</>
            ) : (
              <><ArrowUpDown className="w-4 h-4 mr-2" /> Set Up Territory Plan</>
            )}
          </button>
          <button onClick={() => setShowBulkUpload(true)} className="btn-secondary !py-2.5 !px-4 !text-sm">
            <Upload className="w-4 h-4 mr-2" /> Upload CSV
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary !py-2.5 !px-4 !text-sm"><Plus className="w-4 h-4 mr-2" /> Add Account</button>
        </div>
      </div>

      <BulkUploadProgressBar key={bulkUploadKey} />

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search accounts..." className="input-field !pl-10 !py-2.5" />
        </div>
      </div>

      {!hasPlan && !tieredView && (
        <div className="mb-6 p-4 bg-app-warning/10 border border-app-warning rounded-lg flex items-center gap-3">
          <Info className="w-5 h-5 text-app-warning flex-shrink-0" />
          <p className="text-sm text-app-warning">
            {membership?.role === 'admin'
              ? <>To enable account tiering, set up the <button onClick={() => organisation && openAdminOrgSettings(organisation.id)} className="font-medium underline hover:text-app-warning">Territory Plan in Organisation Settings</button>.</>
              : 'Ask your organisation admin to set up the Territory Plan to enable account tiering.'
            }
          </p>
        </div>
      )}

      {tiering && (
        <div className="mb-6 p-4 bg-forest-50 border border-forest-200 rounded-lg flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-forest-600 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-forest-800">Tiering your accounts...</p>
            <p className="text-xs text-forest-600 mt-0.5">Each account is being scored against your tiering strategy. This usually takes 15 to 30 seconds.</p>
          </div>
        </div>
      )}

      {tieredView ? (
        <div className="space-y-8">
          {(['A', 'B', 'C'] as const).map(tier => {
            const ta = tieredAccounts[tier];
            const colors = { A: 'bg-forest-600', B: 'bg-app-warning', C: 'bg-app-muted' };
            const alloc = territoryPlan?.allocation || { A: 20, B: 30, C: 50 };
            return (
              <div key={tier}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-8 h-8 ${colors[tier]} rounded-lg flex items-center justify-center text-sm font-bold text-white`}>{tier}</span>
                  <h2 className="text-lg font-semibold text-app-ink">Tier {tier}</h2>
                  <span className="text-xs text-app-muted">({alloc[tier]}% allocation)</span>
                  <span className="text-xs text-app-muted ml-auto">{ta.length} accounts</span>
                </div>
                {ta.length === 0 ? (
                  <div className="p-6 rounded-lg bg-app-canvas border border-app-line text-center"><p className="text-sm text-app-muted">No Tier {tier} accounts</p></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ta.map(a => <AccountCard key={a.id} account={a} onClick={() => navigate(`/app/accounts/${a.id}`)} showTierInfo={showTierInfo === a.id} onToggleTierInfo={() => setShowTierInfo(showTierInfo === a.id ? null : a.id)} showScore onDelete={handleDeleteAccount} role={accountRoles[a.id]} isObjective={objectiveAccountIds.has(a.id)} />)}
                  </div>
                )}
              </div>
            );
          })}
          {tieredAccounts.untiered.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-app-ink mb-4">Untiered</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tieredAccounts.untiered.map(a => <AccountCard key={a.id} account={a} onClick={() => navigate(`/app/accounts/${a.id}`)} showTierInfo={false} onToggleTierInfo={() => {}} showScore={false} onDelete={handleDeleteAccount} role={accountRoles[a.id]} isObjective={objectiveAccountIds.has(a.id)} />)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(a => <AccountCard key={a.id} account={a} onClick={() => navigate(`/app/accounts/${a.id}`)} showTierInfo={false} onToggleTierInfo={() => {}} showScore={false} onDelete={handleDeleteAccount} role={accountRoles[a.id]} isObjective={objectiveAccountIds.has(a.id)} />)}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16">
              <Building2 className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
              <h3 className="text-lg font-medium text-app-ink mb-1">No accounts yet</h3>
              <p className="text-sm text-app-muted mb-4">Add your first account to get started</p>
              <button onClick={() => setShowAddModal(true)} className="btn-primary !py-2.5"><Plus className="w-4 h-4 mr-2" /> Add Account</button>
            </div>
          )}
        </div>
      )}

      <AddAccountModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onAccountAdded={handleAccountAdded} />
      <BulkAccountUpload
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onUploadStarted={() => {
          setBulkUploadKey(k => k + 1);
          setTimeout(loadData, 5000);
        }}
      />
      <NewsroomSidebar isOpen={showNewsroom} onClose={() => setShowNewsroom(false)} />
      </>
      )}
    </div>
  );
}

function AccountCard({ account, onClick, showTierInfo, onToggleTierInfo, showScore, onDelete, role, isObjective }: { account: Account; onClick: () => void; showTierInfo: boolean; onToggleTierInfo: () => void; showScore: boolean; onDelete: (id: string, name: string) => void; role?: string; isObjective?: boolean }) {
  const scoreColor = account.tier_score >= 70
    ? 'text-app-positive'
    : account.tier_score >= 45
    ? 'text-app-warning'
    : 'text-app-muted';

  return (
    <div className={`card-hover p-5 group relative ${isObjective ? 'border-l-4 border-l-forest-400' : ''}`} onClick={onClick}>
      {role !== 'member' && (
      <button
        onClick={e => { e.stopPropagation(); onDelete(account.id, account.company_name); }}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-app-line-strong hover:text-app-critical hover:bg-app-critical/10 opacity-0 group-hover:opacity-100 transition z-10"
        title="Delete account"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      )}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <CompanyLogo url={account.logo_url} websiteUrl={account.website_url} name={account.company_name} />
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-app-ink">{account.company_name}</h3>
              {role === 'member' && (
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-app-focus/10 text-app-focus border border-app-focus">Team</span>
              )}
              {role === 'owner' && (
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-app-warning/10 text-app-warning border border-app-warning">Owner</span>
              )}
            </div>
            {account.industry && <p className="text-xs text-app-muted">{account.industry}</p>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-app-muted">
        {account.employee_count > 0 && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {account.employee_count.toLocaleString()}</span>}
        {account.tier && <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Tier {account.tier}</span>}
        {showScore && account.tier_score > 0 && (
          <span className={`flex items-center gap-1 font-medium ${scoreColor}`}>
            <TrendingUp className="w-3.5 h-3.5" /> {Math.round(account.tier_score)}
          </span>
        )}
      </div>
      <TriggerPill accountId={account.id} orgId={account.org_id} signalsStatus={account.signals_status} />
      {showTierInfo && account.tier_justification && (
        <div className="mt-3 p-3 bg-app-canvas rounded-lg text-xs text-app-muted animate-fade-in" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1.5 mb-1"><Info className="w-3 h-3 text-forest-600" /><span className="font-medium text-forest-700">Tier Justification</span></div>
          {account.tier_justification}
        </div>
      )}
      {account.tier_justification && (
        <button onClick={e => { e.stopPropagation(); onToggleTierInfo(); }} className="mt-2 text-xs text-forest-600 hover:text-forest-700 font-medium">
          {showTierInfo ? 'Hide' : 'Why this tier?'}
        </button>
      )}
    </div>
  );
}

function CompanyLogo({ url, websiteUrl, name }: { url: string; websiteUrl: string; name: string }) {
  const [failed, setFailed] = useState(false);
  const logoSrc = resolveLogoUrl(url, websiteUrl);
  if (!logoSrc || failed) {
    return (
      <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-bold text-forest-600">{name.charAt(0)}</span>
      </div>
    );
  }
  return <img src={logoSrc} alt="" className="w-10 h-10 rounded-lg object-cover bg-app-canvas flex-shrink-0" onError={() => setFailed(true)} />;
}

type MatchedCompany = {
  id: string;
  company_name: string;
  website_url: string;
  ticker_symbol: string;
  logo_url: string;
  industry: string;
  employee_count: number;
};

type ExistingOrgAccount = Account & { _isOrgExisting: true };

// Deterministic check run against the live site before the account is created: it fetches the
// URL and compares what the page calls itself with the name that was typed. Advisory only --
// 'conflict' asks for a confirmation, it never refuses. The thorough check is the identity
// gate in front of enrichment, which needs a web search and cannot run inside this modal.
type SiteCheckResult = {
  verdict: 'match' | 'conflict' | 'unknown';
  site_label: string;
  final_domain: string;
  message: string;
};

function AddAccountModal({ isOpen, onClose, onAccountAdded }: { isOpen: boolean; onClose: () => void; onAccountAdded: (account: Account) => void }) {
  const { organisation, user } = useAuth();
  const { apiPost } = useApi();
  const [companyName, setCompanyName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [ticker, setTicker] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingOrgAccount, setExistingOrgAccount] = useState<ExistingOrgAccount | null>(null);
  const [step, setStep] = useState<'input' | 'confirm_site' | 'confirm_org'>('input');
  const [siteWarning, setSiteWarning] = useState<SiteCheckResult | null>(null);

  const resetModal = () => {
    setCompanyName(''); setWebsiteUrl(''); setTicker('');
    setExistingOrgAccount(null); setStep('input'); setError('');
    setJoinRequestSent(false); setOwnerName(''); setSiteWarning(null);
  };

  const handleClose = () => { resetModal(); onClose(); };

  const submitAccount = async () => {
    try {
      await createNewCompanyAndAccount(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('409') || message.toLowerCase().includes('already exists')) {
        setError('An account with this name already exists in your organisation.');
      } else {
        Sentry.captureException(err, { tags: { context: 'create_account' } });
        setError(err instanceof Error ? err.message : 'Failed to add account');
      }
    }
  };

  const checkForExisting = async () => {
    if (!companyName.trim() || !websiteUrl.trim() || !organisation || !user) return;
    setError('');
    setLoading(true);

    try {
      // Never blocking: a site that will not load, or a check that fails, has to leave the
      // user able to add the account. Everything this endpoint knows, the identity gate in
      // front of enrichment finds out again anyway.
      const check = await apiPost<SiteCheckResult>('/v1/verify-company-site', {
        company_name: companyName.trim(),
        website_url: websiteUrl.trim(),
      }).catch(() => null);

      if (check?.verdict === 'conflict') {
        setSiteWarning(check);
        setStep('confirm_site');
        return;
      }

      await submitAccount();
    } finally {
      setLoading(false);
    }
  };

  const addDespiteWarning = async () => {
    setError('');
    setLoading(true);
    try {
      await submitAccount();
    } finally {
      setLoading(false);
    }
  };

  const [joinRequestSent, setJoinRequestSent] = useState(false);
  const [ownerName, setOwnerName] = useState('');

  const addExistingOrgAccount = async () => {
    if (!existingOrgAccount || !user || !organisation) return;
    setLoading(true);
    setError('');

    try {
      const ownerId = existingOrgAccount.owner_user_id;

      if (ownerId && ownerId !== user.id) {
        await apiPost(`/v1/account/${existingOrgAccount.id}/team-requests`, {
          org_id: organisation.id,
        });

        setJoinRequestSent(true);
        setLoading(false);
        return;
      }

      await apiPost(`/v1/account/${existingOrgAccount.id}/team`, {
        org_id: organisation.id,
        user_id: user.id,
      });

      logAccountActivity(
        existingOrgAccount.id,
        'account_added',
        `Added ${existingOrgAccount.company_name} to their account list`,
      );
      onAccountAdded(existingOrgAccount);
      resetModal();
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'add_existing_account' } });
      setError(err instanceof Error ? err.message : 'Failed to add account to your list');
    } finally {
      setLoading(false);
    }
  };

  const createNewCompanyAndAccount = async (_existingCompanyId: string | null, matched?: MatchedCompany | null) => {
    if (!organisation || !user) return;

    const logoUrl = matched?.logo_url || getLogoUrl(websiteUrl);
    const finalName = matched ? matched.company_name : companyName.trim();
    const finalUrl = matched ? matched.website_url : websiteUrl.trim();
    const finalTicker = matched ? (matched.ticker_symbol || ticker.trim()) : ticker.trim();

    const { account } = await apiPost<{ account: Account }>('/v1/account', {
      org_id: organisation.id,
      company_name: finalName,
      website_url: finalUrl,
      ticker_symbol: finalTicker,
      logo_url: logoUrl,
      industry: matched?.industry || '',
      employee_count: matched?.employee_count ?? null,
    });

    logAccountActivity(account.id, 'account_added', `Added ${finalName} to their account list`);

    apiPost('/v1/enrich-account', {
      account_id: account.id,
      company_name: finalName,
      website: finalUrl,
      website_url: finalUrl,
      ticker: finalTicker,
      ticker_symbol: finalTicker,
    }).catch((err: unknown) => { Sentry.captureException(err, { tags: { context: 'enrich-account' } }); });

    onAccountAdded(account);
    resetModal();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Account" size="md">
      {step === 'input' ? (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">Corporation Name *</label>
            <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="input-field" placeholder="e.g. Salesforce, Microsoft" />
          </div>
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">Website URL *</label>
            <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} className="input-field" placeholder="https://company.com" />
            <p className="text-xs text-app-muted mt-1">Required for accurate company identification</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-app-ink mb-2">Ticker Symbol (optional)</label>
            <input type="text" value={ticker} onChange={e => setTicker(e.target.value)} className="input-field" placeholder="e.g. CRM, MSFT" />
          </div>
          {error && (
            <div className="p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <p className="text-sm text-app-critical">{error}</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={handleClose} className="btn-ghost flex-1">Cancel</button>
            <button onClick={checkForExisting} disabled={!companyName.trim() || !websiteUrl.trim() || loading} className="btn-primary flex-1">
              <Search className="w-4 h-4 mr-2" />{loading ? 'Checking...' : 'Search & Add'}
            </button>
          </div>
        </div>
      ) : step === 'confirm_site' && siteWarning ? (
        <div className="space-y-5">
          <div className="p-4 bg-app-warning/10 border border-app-warning rounded-lg">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5 text-app-warning" />
              <div>
                <p className="text-sm font-semibold text-app-warning">This website belongs to a different company</p>
                <p className="text-sm text-app-warning mt-1">{siteWarning.message}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-app-ink">Website URL</label>
            <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} className="input-field" placeholder="https://company.com" />
            <p className="text-xs text-app-muted">
              Correcting the URL here re-runs the check. All research for {companyName.trim()} is generated from this website.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <p className="text-sm text-app-critical">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setSiteWarning(null); setStep('input'); setError(''); }}
              disabled={loading}
              className="btn-ghost flex-1"
            >
              Back
            </button>
            <button onClick={checkForExisting} disabled={loading || !websiteUrl.trim()} className="btn-primary flex-1">
              <Search className="w-4 h-4 mr-2" />{loading ? 'Checking...' : 'Re-check'}
            </button>
          </div>
          <button onClick={addDespiteWarning} disabled={loading} className="text-xs font-medium text-app-muted underline underline-offset-2 w-full text-center">
            Add it anyway. The name and website are correct.
          </button>
        </div>
      ) : existingOrgAccount ? (
        <div className="space-y-5">
          {joinRequestSent ? (
            <>
              <div className="p-6 bg-app-positive/10 border border-app-positive rounded-lg text-center">
                <div className="w-12 h-12 bg-app-positive/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-app-positive" />
                </div>
                <p className="text-sm font-semibold text-app-positive mb-1">Request sent to {ownerName}</p>
                <p className="text-xs text-app-positive">
                  You will be notified when the account owner approves your request. The account will then appear in your grid automatically.
                </p>
              </div>
              <button onClick={handleClose} className="btn-primary w-full">Done</button>
            </>
          ) : (
            <>
              <div className={`p-4 ${existingOrgAccount.owner_user_id && existingOrgAccount.owner_user_id !== user?.id ? 'bg-app-focus/10 border-app-focus' : 'bg-app-positive/10 border-app-positive'} border rounded-lg`}>
                <p className="text-sm font-medium text-app-ink mb-3">Account already exists in your organisation</p>
                <div className="flex items-center gap-3 mb-3">
                  <CompanyLogo url={existingOrgAccount.logo_url} websiteUrl={existingOrgAccount.website_url} name={existingOrgAccount.company_name} />
                  <div>
                    <p className="text-sm font-semibold text-app-ink">{existingOrgAccount.company_name}</p>
                    {existingOrgAccount.website_url && (
                      <p className="text-xs text-app-muted">{existingOrgAccount.website_url}</p>
                    )}
                  </div>
                </div>
                {(existingOrgAccount.industry || existingOrgAccount.employee_count > 0) && (
                  <div className="flex items-center gap-3 text-xs text-app-muted">
                    {existingOrgAccount.industry && <span>{existingOrgAccount.industry}</span>}
                    {existingOrgAccount.employee_count > 0 && <span>{existingOrgAccount.employee_count.toLocaleString()} employees</span>}
                  </div>
                )}
                {existingOrgAccount.enrichment_status === 'completed' && (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-app-positive/10 rounded-lg">
                    <div className="w-2 h-2 bg-app-positive rounded-full" />
                    <p className="text-xs text-app-positive font-medium">Research data already populated</p>
                  </div>
                )}
                {existingOrgAccount.owner_user_id && existingOrgAccount.owner_user_id !== user?.id ? (
                  <p className="text-xs text-app-focus mt-3">
                    This account is owned by <span className="font-medium">{ownerName}</span>. You can request to join their team and gain access to all existing research data.
                  </p>
                ) : (
                  <p className="text-xs text-app-positive mt-3">
                    A colleague has already researched this account. Adding it to your list will give you access to all existing data without reprocessing.
                  </p>
                )}
              </div>
              {error && (
                <div className="p-3 bg-app-critical/10 border border-app-critical rounded-lg">
                  <p className="text-sm text-app-critical">{error}</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setStep('input'); setExistingOrgAccount(null); setError(''); }} className="btn-ghost flex-1">Back</button>
                {existingOrgAccount.owner_user_id && existingOrgAccount.owner_user_id !== user?.id ? (
                  <button onClick={addExistingOrgAccount} disabled={loading} className="btn-primary flex-1">
                    <Users className="w-4 h-4 mr-2" />{loading ? 'Sending...' : 'Request to Join Team'}
                  </button>
                ) : (
                  <button onClick={addExistingOrgAccount} disabled={loading} className="btn-primary flex-1">
                    <Plus className="w-4 h-4 mr-2" />{loading ? 'Adding...' : 'Add to My Accounts'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
